import { MeshGradient, Warp } from "@paper-design/shaders-react";
import { memo } from "react";
import type { ReactNode } from "react";

import { useLiveShader } from "./ambient.ts";
import { MarbleBackdrop } from "./marble-backdrop.tsx";
import { ALARM_WARP, WARP_SPEED } from "./warp-presets.ts";

// The landing's own paint behind one hero on a page (the home greeting): marble by day, the
// Deep tide by night, exactly as the landing and the sign-in pages show it, faded out at its
// edges so the content sits in it instead of on top of a picture. Opt-in: nothing else in the
// design system glows.

const GlowLayer = memo(function GlowLayer({ wide }: { readonly wide: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={
        wide
          ? // Edge to edge of the page's main area, whatever the content width (--bleed-width, set by the app frame).
            // At most one screen tall (D1072): it never binds while the page fits the screen, so the
            // approved look is untouched; on a longer page the glow stays a screen, not a smear.
            "pointer-events-none absolute -top-8 -bottom-8 left-1/2 -z-10 max-h-svh w-(--bleed-width,100vw) -translate-x-1/2 mask-[radial-gradient(75%_75%_at_50%_42%,#000_45%,transparent_100%)]"
          : "pointer-events-none absolute -inset-x-8 -top-8 -bottom-6 -z-10 mask-[radial-gradient(64%_68%_at_50%_44%,#000_40%,transparent_100%)]"
      }
    >
      <MarbleBackdrop focus="center" />
    </div>
  );
});

export function ShaderGlow({
  children,
  wide = false,
}: {
  readonly children: ReactNode;
  /** Reach the edges of the app's main area, and grow to fill the page's height. */
  readonly wide?: boolean;
}) {
  return (
    <div className={wide ? "relative isolate flex flex-1 flex-col" : "relative isolate"}>
      <GlowLayer wide={wide} />
      {children}
    </div>
  );
}

/**
 * A quiet moving wash inside a surface (a banner, a card), in the surface's
 * own colour and the brand's. It overhangs its parent so the blur's soft rim
 * is clipped away; the parent clips it and sits above `-z-10`.
 */
export const GlowFill = memo(function GlowFill({ tokens }: { readonly tokens: readonly string[] }) {
  const shader = useLiveShader(tokens);
  if (!shader) return null;
  return (
    <MeshGradient
      aria-hidden="true"
      className="pointer-events-none absolute -inset-12 -z-10 animate-fade-in opacity-20 blur-lg"
      colors={shader.colors}
      distortion={0.7}
      swirl={0.3}
      // Wide, short surfaces stretch the mesh; zoomed out, its colours spread along the whole banner.
      fit="cover"
      scale={0.45}
      speed={shader.still ? 0 : 0.25}
      minPixelRatio={1}
      maxPixelCount={800 * 200}
    />
  );
});

const ALARM_TOKENS = ["alarm-1", "alarm-2", "alarm-3"] as const;

/** The alarm Warp behind an error that needs the reader now. Stronger than a glow: it is the error. */
export const AlarmFill = memo(function AlarmFill() {
  const shader = useLiveShader(ALARM_TOKENS);
  if (!shader) return null;
  return (
    <Warp
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 size-full animate-fade-in opacity-60"
      colors={shader.colors}
      {...ALARM_WARP}
      speed={shader.still ? 0 : WARP_SPEED}
      minPixelRatio={1}
      maxPixelCount={800 * 200}
    />
  );
});
